import { redirect } from "next/navigation";

import {
  serializeFlatQueryParams,
  serializeNestedQueryParams
} from "@/utils/functions";
import { ongsAdapter } from "./ongs.adapter";

import type {
  Ong,
  OngAPI,
  OngsPage,
  OngsQueryParams,
  PostProductCartProps,
  ProductsAPI,
  ProductsQueryParams,
  RemoveProductCartProps,
  Products,
  GetOngProductsProps
} from "./ongs.types";
import type { Page } from "@/types/Page";

const revalidate = parseInt(process.env.REVALIDATE || "600", 10); // Cache for 10 minutes

export async function postProductCart({
  orderFormId,
  products
}: PostProductCartProps) {
  const orderItems = products.map((product) => ({
    seller: product.seller,
    quantity: product.quantity,
    id: product.id
  }));

  let updatedCart = { orderItems };

  try {
    const response = await fetch(
      `${process.env.BASE_API_URL}checkout/cart/${orderFormId}/items`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(updatedCart)
      }
    );

    if (!response.ok) {
      throw new Error(
        `Erro ao adicionar produto ao carrinho: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    return data;
  } catch (error: unknown) {
    console.error("Erro em postProductCart:", error);
  }
}

export async function removeProductCart({
  orderFormId,
  sellerId,
  sku,
  quantity,
  index
}: RemoveProductCartProps) {
  const newProduct = [
    {
      seller: sellerId,
      id: sku,
      quantity,
      index
    }
  ];

  let updatedCart = { orderItems: newProduct };

  try {
    const response = await fetch(
      `${process.env.APP_API_URL}checkout/cart/${orderFormId}/items`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(updatedCart)
      }
    );

    if (!response.ok) {
      throw new Error(
        `Erro ao remover produto do carrinho: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    return data;
  } catch (error: unknown) {
    console.error("Erro em removeProductCart:", error);
  }
}

export async function getOngBySlug(slug: string): Promise<Page<Ong>> {
  try {
    const response: Response = await fetch(
      `${process.env.CMS_HOST}api/ongs?filters[slug][$eq]=${slug}&populate[logo][fields][0]=url&populate[logo][fields][1]=alternativeText&populate[gallery][fields][0]=url&populate[gallery][fields][1]=alternativeText&populate[loja][fields][0]=name&populate[loja][populate][address][fields][0]=zipcode&populate[Link]=true&fields[0]=name&fields[1]=description&fields[2]=long_description&fields[3]=since&fields[4]=state&fields[5]=city&fields[6]=vtex_pickup_id&fields[7]=sw&fields[8]=collection`,
      {
        next: { revalidate }
      }
    );

    if (!response.ok) {
      throw new Error(
        `Erro ao buscar ong: ${response.status} ${response.statusText}`
      );
    }

    const ongAPI: OngAPI = await response.json();

    if (!ongAPI || !ongAPI.data) {
      throw new Error("Dados da ong não encontrados ou inválidos.");
    }

    return {
      meta: ongsAdapter.toMetaData(ongAPI),
      data: ongsAdapter.toOng(ongAPI)
    };
  } catch (error: unknown) {
    console.error("Erro em getOngBySlug:", error);
    redirect("/404");
  }
}

export async function getOngProducts({
  queryParams,
  paths
}: GetOngProductsProps): Promise<Page<Products>> {
  try {
    const defaultQueryParams: Partial<ProductsQueryParams> = {
      ...queryParams,
      count: 12,
      simulationBehavior: "default",
      hideUnavailableItem: true,
      showSponsored: false
    };

    const queryString = serializeFlatQueryParams(defaultQueryParams);

    const { sw, collection } = paths;

    let endpoint = `${process.env.BASE_API_URL}catalog/IS/${sw}`;

    if (collection) {
      endpoint += `/${collection}`;
    }

    endpoint += `?${queryString}`;

    const response: Response = await fetch(endpoint, { next: { revalidate } });

    if (!response.ok) {
      throw new Error(
        `Erro ao buscar produtos: ${response.status} ${response.statusText}`
      );
    }

    const ongProductsAPI: ProductsAPI = await response.json();

    if (!ongProductsAPI) {
      throw new Error("Dados da ong não encontrados ou inválidos.");
    }

    return {
      meta: ongsAdapter.toProductsMetaData(ongProductsAPI),
      data: ongsAdapter.toProducts(ongProductsAPI)
    };
  } catch (error: unknown) {
    console.error("Erro em getOngProducts", error);
    redirect("/404");
  }
}

export async function getOngsStateCity(queryParams?: OngsQueryParams) {
  try {
    const defaultQueryParams: OngsQueryParams = {
      populate: {
        logo: {
          fields: ["url", "alternativeText"]
        },
        gallery: {
          fields: ["url", "alternativeText"]
        },
        loja: {
          fields: ["name"],
          populate: {
            address: {
              fields: ["zipcode"]
            }
          }
        },
        Link: true
      },
      fields: [
        "name",
        "description",
        "long_description",
        "since",
        "state",
        "city",
        "vtex_pickup_id",
        "sw",
        "slug"
      ],
      ...queryParams
    };

    const queryString = serializeNestedQueryParams(defaultQueryParams);

    const response: Response = await fetch(
      `${process.env.CMS_HOST}api/ongs?${queryString}`,
      {
        next: { revalidate }
      }
    );

    if (!response.ok) {
      throw new Error(
        `Erro ao buscar a lista de ongs: ${response.status} ${response.statusText}`
      );
    }

    const ongsAPI: OngAPI = await response.json();

    if (!ongsAPI || !ongsAPI.data) {
      throw new Error("Dados das ongs não encontrados ou inválidos.");
    }

    return {
      data: ongsAdapter.toListOngsStateCity(ongsAPI),
      meta: ongsAdapter.toOngsMetaData(ongsAPI)
    };
  } catch (error: unknown) {
    console.error("Erro em getOngsStateCity:", error);
    redirect("/404");
  }
}

export async function getOngs(
  queryParams?: OngsQueryParams
): Promise<Page<Ong[]>> {
  try {
    const defaultQueryParams: OngsQueryParams = {
      populate: {
        logo: {
          fields: ["url", "alternativeText"]
        },
        gallery: {
          fields: ["url", "alternativeText"]
        },
        loja: {
          fields: ["name"],
          populate: {
            address: {
              fields: ["zipcode"]
            }
          }
        },
        Link: true
      },
      fields: [
        "name",
        "description",
        "long_description",
        "since",
        "state",
        "city",
        "vtex_pickup_id",
        "sw",
        "slug"
      ],
      ...queryParams
    };

    const queryString = serializeNestedQueryParams(defaultQueryParams);

    const response: Response = await fetch(
      `${process.env.CMS_HOST}api/ongs?${queryString}`,
      {
        next: { revalidate }
      }
    );

    if (!response.ok) {
      throw new Error(
        `Erro ao buscar a lista de ongs: ${response.status} ${response.statusText}`
      );
    }

    const ongsAPI: OngAPI = await response.json();

    if (!ongsAPI || !ongsAPI.data) {
      throw new Error("Dados das ongs não encontrados ou inválidos.");
    }

    return {
      data: ongsAdapter.toListOngs(ongsAPI),
      meta: ongsAdapter.toOngsMetaData(ongsAPI)
    };
  } catch (error: unknown) {
    console.error("Erro em getOngs:", error);
    redirect("/404");
  }
}

export async function getOngsPage(): Promise<Page<OngsPage>> {
  try {
    const response: Response = await fetch(
      `${process.env.CMS_HOST}api/ongs-page?populate[banner][fields][0]=id&populate[banner][fields][1]=url&populate[banner][fields][2]=alternativeText&populate[bannerMobile][fields][0]=id&populate[bannerMobile][fields][1]=url&populate[bannerMobile][fields][2]=alternativeText`,
      {
        next: { revalidate }
      }
    );

    if (!response.ok) {
      throw new Error(
        `Erro ao buscar os dados da página de ONGs: ${response.status} ${response.statusText}`
      );
    }

    const ongsPageAPI = await response.json();

    if (!ongsPageAPI || !ongsPageAPI.data) {
      throw new Error("Dados da página de ONGs não encontrados ou inválidos.");
    }

    return {
      data: ongsAdapter.toOngsPage(ongsPageAPI)
    };
  } catch (error: unknown) {
    console.error("Erro em getOngsPage:", error);
    redirect("/404");
  }
}
